import React, { useEffect, useState } from "react";
import { Avatar, Box, Button, Card, CardContent, Stack, Typography } from "@mui/material";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import { verifyUser, useAuth, isUserVerified } from "@shadowhealth/oidc-login";
import { enqueueSnackbar } from "notistack";

const LoginPage = () => {
  const auth = useAuth();
  const [verified, setVerified] = useState(null);
  const [loginError, setLoginError] = useState(null);

  useEffect(() => {
    (async () => {
      if (auth.isAuthenticated) {
        if (verified == null) {
          // oidc-login's configure() roleConfig (see src/index.jsx) checks
          // the viewer/admin Keycloak client role (PROG-65578), mirroring
          // convovec-core's protected_router check. Under FAKE_AUTH,
          // oidc-login's own fake verifyUser bypasses this automatically.
          await verifyUser(auth.user?.access_token);
          const isVerified = isUserVerified();
          setVerified(isVerified);
          if (!isVerified) {
            setLoginError("Not authorized for this application");
            auth.removeUser();
          }
        }
      } else if (loginError) {
        enqueueSnackbar(loginError, { variant: "error" });
        setLoginError(null);
      }
    })();
  }, [auth]);

  if (auth.activeNavigator) {
    return (
      <Stack alignItems="center" justifyContent="center" sx={{ py: 8 }}>
        <Typography variant="h6">Logging you in…</Typography>
      </Stack>
    );
  }

  if (auth.isAuthenticated && verified) {
    return (
      <Card sx={{ maxWidth: 520, mx: "auto", mt: 6 }}>
        <CardContent>
          <Stack direction="row" alignItems="center" spacing={2}>
            <Avatar sx={{ bgcolor: "primary.main" }}>{auth.user?.profile?.email?.[0]?.toUpperCase() || "U"}</Avatar>
            <Box sx={{ flexGrow: 1 }}>
              <Typography variant="subtitle1">Signed in as</Typography>
              <Typography variant="body2" color="text.secondary">
                {auth.user?.profile?.email}
              </Typography>
            </Box>
            <Button variant="outlined" color="secondary" onClick={auth.removeUser}>
              Logout
            </Button>
          </Stack>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card sx={{ maxWidth: 420, mx: "auto", mt: 8 }}>
      <CardContent>
        <Stack alignItems="center" spacing={2} sx={{ py: 2 }}>
          <Avatar sx={{ bgcolor: "primary.main", width: 56, height: 56 }}>
            <LockOutlinedIcon />
          </Avatar>
          <Typography variant="h5" component="h1">
            Welcome to ConvoVec
          </Typography>
          <Typography variant="body2" color="text.secondary" align="center">
            Sign in with your Elsevier account to manage scripts and conversations.
          </Typography>
          <Button
            size="large"
            variant="contained"
            color="primary"
            onClick={auth.signinRedirect}
            sx={{ mt: 2, minWidth: 240 }}
          >
            Login with Elsevier
          </Button>
        </Stack>
      </CardContent>
    </Card>
  );
};

export default LoginPage;
